import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Flashcard, UserProgress, StudyTechnique } from '../../types';
import { SpacedRepetition } from '../../utils/spacedRepetition';
import { MarkdownContent } from '../MarkdownContent';

const StudyContainer = styled(Box)({
  maxWidth: 920,
  margin: '0 auto',
  padding: '28px',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: '24px',
  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
  backdropFilter: 'blur(18px)',
});

const FeynmanCard = styled(Card)({
  marginBottom: '20px',
  minHeight: '250px',
  borderRadius: '20px',
  border: '1px solid var(--card-border)',
  background: 'rgba(255, 255, 255, 0.9)',
});

const ExplanationInput = styled(TextField)({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
  },
});

const StepContainer = styled(Box)({
  marginBottom: '16px',
  padding: '16px',
  borderRadius: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  border: '1px solid var(--card-border)',
});

interface FeynmanTechniqueProps {
  flashcards: Flashcard[];
  userProgress: UserProgress[];
  onProgressUpdate: (progress: UserProgress) => void;
  onSessionComplete: (sessionData: any) => void;
}

export const FeynmanTechnique: React.FC<FeynmanTechniqueProps> = ({
  flashcards,
  userProgress,
  onProgressUpdate,
  onSessionComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [step, setStep] = useState<'explain' | 'identify' | 'simplify' | 'review'>('explain');
  const [explanation, setExplanation] = useState('');
  const [gaps, setGaps] = useState<string[]>([]);
  const [simplified, setSimplified] = useState('');
  const [startTime] = useState(Date.now());
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showHints, setShowHints] = useState(false);

  const currentCard = flashcards[currentIndex];
  const progress = ((currentIndex + 1) / flashcards.length) * 100;
  const currentProgress = userProgress.find(p => p.flashcardId === currentCard?.id);

  const feynmanSteps = [
    {
      title: "Step 1: Explain the Concept",
      description: "Explain the concept in simple terms as if teaching it to a child. Use your own words.",
      icon: <SchoolIcon />,
      field: explanation,
      setter: setExplanation,
      placeholder: "Explain this concept in simple terms..."
    },
    {
      title: "Step 2: Identify Knowledge Gaps",
      description: "Review your explanation and identify areas where you're uncertain or incomplete.",
      icon: <LightbulbIcon />,
      field: gaps.join('\n'),
      setter: (value: string) => setGaps(value.split('\n').filter(gap => gap.trim())),
      placeholder: "List the areas you feel uncertain about..."
    },
    {
      title: "Step 3: Simplify Further",
      description: "Simplify your explanation even more, using analogies if helpful.",
      icon: <SchoolIcon />,
      field: simplified,
      setter: setSimplified,
      placeholder: "Simplify your explanation using analogies..."
    },
    {
      title: "Step 4: Review the Correct Answer",
      description: "Compare your explanation with the expert answer and note key differences.",
      icon: <LightbulbIcon />,
      readOnly: true
    }
  ];

  useEffect(() => {
    if (sessionComplete) {
      const sessionData = {
        technique: StudyTechnique.FEYNMAN,
        flashcardsStudied: flashcards.length,
        explanationsCompleted: flashcards.length,
        totalTime: Math.floor((Date.now() - startTime) / 60000),
        gapsIdentified: gaps.length
      };
      onSessionComplete(sessionData);
    }
  }, [sessionComplete, flashcards.length, startTime, gaps.length, onSessionComplete]);

  const handleNextStep = () => {
    switch (step) {
      case 'explain':
        if (explanation.trim()) {
          setStep('identify');
        }
        break;
      case 'identify':
        setStep('simplify');
        break;
      case 'simplify':
        if (simplified.trim()) {
          setStep('review');
        }
        break;
      case 'review':
        handleCompleteCard();
        break;
    }
  };

  const handleCompleteCard = () => {
    // Evaluate the quality of the Feynman explanation
    const explanationQuality = evaluateExplanation();
    const quality = explanationQuality >= 3 ? 4 : 2;

    if (currentProgress) {
      const updatedProgress = SpacedRepetition.calculateNextReview(currentProgress, quality);
      onProgressUpdate(updatedProgress);
    }

    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetCard();
    } else {
      setSessionComplete(true);
    }
  };

  const resetCard = () => {
    setStep('explain');
    setExplanation('');
    setGaps([]);
    setSimplified('');
    setShowHints(false);
  };

  const evaluateExplanation = (): number => {
    // Simple evaluation based on explanation length and content
    let score = 0;
    if (explanation.length > 50) score += 1;
    if (gaps.length > 0) score += 1;
    if (simplified.length > 30) score += 1;
    if (explanation.toLowerCase().includes('because') || explanation.toLowerCase().includes('therefore')) score += 1;
    return score;
  };

  if (sessionComplete) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Dialog open={true} maxWidth="sm" fullWidth>
            <DialogTitle>Feynman Technique Session Complete!</DialogTitle>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Session Summary
              </Typography>
              <Typography>Concepts explained: {flashcards.length}</Typography>
              <Typography>Knowledge gaps identified: {gaps.length}</Typography>
              <Typography>Time spent: {Math.floor((Date.now() - startTime) / 60000)} minutes</Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Great job using the Feynman Technique! Explaining concepts simply helps solidify your understanding.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => window.location.reload()}>Start New Session</Button>
            </DialogActions>
          </Dialog>
        </StudyContainer>
      </motion.div>
    );
  }

  if (!currentCard) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <StudyContainer>
          <Typography>No flashcards available for study.</Typography>
        </StudyContainer>
      </motion.div>
    );
  }

  const currentStep = feynmanSteps.find(s => {
    switch (step) {
      case 'explain': return s.title.includes('Step 1');
      case 'identify': return s.title.includes('Step 2');
      case 'simplify': return s.title.includes('Step 3');
      case 'review': return s.title.includes('Step 4');
      default: return false;
    }
  });

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      <StudyContainer>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Feynman Technique Practice
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Explain concepts in simple terms as if teaching them to someone else. This technique helps identify and fill knowledge gaps.
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Card {currentIndex + 1} of {flashcards.length} • Step: {currentStep?.title}
          </Typography>
        </Box>

        <FeynmanCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Chip label={currentCard.topic} color="primary" size="small" />
              <Chip label={`Difficulty: ${currentCard.difficulty}`} size="small" />
            </Box>

          <Typography variant="h5" gutterBottom>
            {currentCard.question}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {currentCard.tags.map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        </CardContent>
      </FeynmanCard>

      <Box sx={{ mb: 3 }}>
        {feynmanSteps.map((stepInfo, index) => (
          <StepContainer key={index}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {stepInfo.icon}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {stepInfo.title}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {stepInfo.description}
            </Typography>

            {step === stepInfo.title.toLowerCase().replace(/\s+/g, '').slice(-6) && (
              stepInfo.readOnly ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Correct Answer:
                  </Typography>
                  <MarkdownContent content={currentCard.answer} />
                  <Button
                    variant="contained"
                    onClick={handleNextStep}
                    sx={{ mt: 2 }}
                  >
                    Complete Card
                  </Button>
                </Box>
              ) : (
                <ExplanationInput
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={stepInfo.placeholder}
                  value={stepInfo.field}
                  onChange={(e) => stepInfo.setter && stepInfo.setter(e.target.value)}
                  variant="outlined"
                  disabled={stepInfo.readOnly}
                />
              )
            )}
          </StepContainer>
        ))}
      </Box>

      {step !== 'review' && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => setShowHints(!showHints)}
          >
            {showHints ? 'Hide Hints' : 'Show Hints'}
          </Button>
          <Button
            variant="contained"
            onClick={handleNextStep}
            disabled={
              (step === 'explain' && !explanation.trim()) ||
              (step === 'simplify' && !simplified.trim())
            }
          >
            Next Step
          </Button>
        </Box>
      )}

        {showHints && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Feynman Technique Tips</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                • Use simple language and analogies<br/>
                • Explain the "why" behind concepts<br/>
                • Identify areas where your understanding is fuzzy<br/>
                • Keep simplifying until you can explain it to a 5-year-old<br/>
                • Fill knowledge gaps by researching or reviewing
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}
      </StudyContainer>
    </motion.div>
  );
};
